import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from './firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { generateTrackingNumber } from './simple-tracking-generator';
import { useStaffNames, STAFF_CONFIG_PATH } from './hooks/useStaffNames';

const AddPackage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [user] = useAuthState(auth);
  const [showConfirm, setShowConfirm] = useState(false);
  const [generatedTrackingNumber, setGeneratedTrackingNumber] = useState(generateTrackingNumber());
  const [form, setForm] = useState({
    trackingNumber: generatedTrackingNumber,
    sender: '',
    recipient: '',
    phone: '',
    destination: '',
    details: '',
    status: 'in-store',
    assignedTo: '', // Add assignedTo to form state
    company: '',
    createdBy: '', // Staff member tagging themselves as the creator (shared login)
  });
  const [users, setUsers] = useState([]);
  const { staffNames, loading: staffLoading, error: staffError } = useStaffNames();

  const navigate = useNavigate();

  // Company options for dropdown
  const companyOptions = ['Limitless Fashions', 'Jenz', 'Denim World'];

  // Status options for dropdown
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'returned', label: 'Returned' },
    { value: 'left-store', label: 'Left Store' },
    { value: 'in-store', label: 'In Store' },
  ];

  // Fetch all users with the 'driver' role so the dropdown always reflects
  // whoever currently holds that role - no hardcoded fallback driver.
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const q = query(collection(db, 'users'), where('roles', 'array-contains', 'driver'));
        const snapshot = await getDocs(q);
        const drivers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(drivers);
      } catch (error) {
        console.error('Error fetching drivers:', error);
      }
    };
    fetchDrivers();
  }, []);

  // Get status info for styling
  const getStatusInfo = (status) => {
    const statusTypes = {
      'delivered': { 
        color: 'bg-green-100 text-green-800',
        icon: (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )
      },
      'in-transit': { 
        color: 'bg-blue-100 text-blue-800',
        icon: (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
        )
      },
      'pending': { 
        color: 'bg-gray-100 text-gray-800',
        icon: (
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      'delayed': { 
        color: 'bg-yellow-100 text-yellow-800',
        icon: (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      },
      'returned': { 
        color: 'bg-yellow-100 text-orange-800',
        icon: (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        )
      },
      'left-store': { 
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 text-blue-500" fill="blue" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
          <path d="M200-120q-50 0-85-35t-35-85q0-50 35-85t85-35h560q50 0 85 35t35 85q0 50-35 85t-85 35H200Zm0-80h560q17 0 28.5-11.5T800-240q0-17-11.5-28.5T760-280H200q-17 0-28.5 11.5T160-240q0 17 11.5 28.5T200-200Zm200-240q-17 0-28.5-11.5T360-480v-320q0-17 11.5-28.5T400-840h320q17 0 28.5 11.5T760-800v320q0 17-11.5 28.5T720-440H400Zm40-80h240v-240H440v240Zm-360-2v-77h197v77H80Zm400-118h160v-79H480v79Zm-320 0h117v-79H160v79Zm280 120v-240 240Z"/>
        </svg>
      )
      },
      'in-store': { 
        color: 'bg-gray-100 text-gray-800',
        icon: (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75"></path>
          </svg>
        )
      },
      'canceled': { 
      color: 'bg-red-100 text-red-800',
      icon: (
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"></path>
        </svg>
      )
      }
    };
    
    return statusTypes[status?.toLowerCase()] || statusTypes['pending'];
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate total is a number
    if (isNaN(Number(form.total)) || form.total === "") {
      alert("Please enter a valid number for Total.");
      return;
    }
    // The Created By select is disabled while the staff list loads (or if it
    // failed to load), and browsers skip validation on disabled fields.
    if (!form.createdBy) {
      alert("Please select the staff member creating this package.");
      return;
    }
    setShowConfirm(true);
  };

  const handleGenerateNewTracking = () => {
    const newTracking = generateTrackingNumber();
    setGeneratedTrackingNumber(newTracking);
    setForm({ ...form, trackingNumber: newTracking });
  };

  const handleConfirmSubmit = async () => {
    if (!user){
      alert("You must be logged in to add a package")
      return;
    }
    setIsLoading(true);
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, 'packages'), {
        ...form,
        createdAt: now,
        updatedAt: now,
        assignedTo: form.assignedTo,
        createdBy: form.createdBy,
        history: [{ 
          status: form.status, 
          timestamp: now,
          note: 'Package created',
        }]
      });
      navigate('/packages');
    } catch (error) {
      console.error("Error adding package:", error);
      setIsLoading(false);
      setShowConfirm(false);
      alert("Failed to add package. Please try again.");
    }
  };

  const statusInfo = getStatusInfo(form.status);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 p-6 border-b">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Add New Package</h1>
              <p className="text-gray-600 mt-1">Create a new package in the tracking system</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                {statusInfo.icon}
                Initial Status: {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex flex-col space-y-4 max-w-2xl mx-auto">
            {/* Tracking Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="trackingNumber"
                  value={form.trackingNumber}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded font-mono"
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleGenerateNewTracking}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                  title="Generate new tracking number"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Auto-generated tracking number (click refresh to generate a new one)</p>
            </div>

            {/* Sender */}
            <div>
              <label htmlFor="sender" className="block text-sm font-medium text-gray-700 mb-1">Sender</label>
              <input
                type="text"
                id="sender"
                name="sender"
                value={form.sender}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter sender name"
                required
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                id="company"
                name="company"
                value={form.company}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              >
                <option value="">Select Company...</option>
                {companyOptions.map(company => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>

            {/* Recipient */}
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
              <input
                type="text"
                id="recipient"
                name="recipient"
                value={form.recipient}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter recipient name"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone#</label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter phone number"
                required
              />
            </div>

            {/* Details */}
            <div>
              <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <input
                type="text"
                id="details"
                name="details"
                value={form.details}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter package details"
                required
              />
            </div>

            {/* Destination */}
            <div>
              <label htmlFor="destination" className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
              <input
                type="text"
                id="destination"
                name="destination"
                value={form.destination}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter destination location"
                required
              />
            </div>

            {/* Total */}
            <div>
              <label htmlFor="total" className="block text-sm font-medium text-gray-700 mb-1">Total</label>
              <input
                type="number"
                id="total"
                name="total"
                value={form.total}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Total: TT$"
                required
                min="0"
                step="0.01"
                inputMode="decimal"
                pattern="^\d+(\.\d{1,2})?$"
                onKeyDown={e => {
                  if (
                    ['e', 'E', '+', '-'].includes(e.key)
                    || (e.key === '.' && form.total.includes('.'))
                  ) {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Initial Status</label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Driver Assigned */}
            <div>
              <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">Driver Assigned</label>
              <select
                id="assignedTo"
                name="assignedTo"
                value={form.assignedTo || ''}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              >
                <option value="">Select Driver...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.email || user.displayName || user.id}
                  </option>
                ))}
              </select>
            </div>

            {/* Created By - staff share a central login, so they tag themselves here */}
            <div>
              <label htmlFor="createdBy" className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
              <select
                id="createdBy"
                name="createdBy"
                value={form.createdBy}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={staffLoading || staffNames.length === 0}
              >
                <option value="">
                  {staffLoading ? 'Loading staff...' : 'Select Staff Member...'}
                </option>
                {staffNames.map(name => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              {staffError ? (
                <p className="text-xs text-red-600 mt-1">
                  Could not load the staff list. Refresh the page, or check the <span className="font-mono">{STAFF_CONFIG_PATH}</span> document.
                </p>
              ) : !staffLoading && staffNames.length === 0 ? (
                <p className="text-xs text-red-600 mt-1">
                  No staff names are set up yet. Add a <span className="font-mono">names</span> array to the <span className="font-mono">{STAFF_CONFIG_PATH}</span> document in Firestore.
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Select your name so the package is credited to you</p>
              )}
            </div>

            {/* Status Info */}
            <div className="mt-4">
              <div className="flex items-center text-yellow-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium">Package will be created with status: {form.status}</span>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/packages')}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Add Package
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-blue-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-bold">Confirm Package Creation</h3>
            </div>
            <p className="mb-2">
              You are about to add a new package with the following details:
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4 text-sm">
              <p><strong>Tracking Number:</strong> {form.trackingNumber}</p>
              <p><strong>Company:</strong> {form.company}</p>
              <p><strong>Sender:</strong> {form.sender}</p>
              <p><strong>Recipient:</strong> {form.recipient}</p>
              <p><strong>Phone:</strong> {form.phone}</p>
              <p><strong>Details:</strong> {form.details}</p>
              <p><strong>Destination:</strong> {form.destination}</p>
              <p><strong>Total:</strong> {form.total}</p>
              <p><strong>Created By:</strong> {form.createdBy}</p>
              <p className="flex items-center mt-1">
                <strong className="mr-1">Status:</strong> 
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                  {statusInfo.icon}
                  {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
                </span>
              </p>
            </div>
            <p className="mb-6 text-sm text-gray-600">
              This will create a new package in the system with a timestamp and status history entry.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPackage;